import React, { useState, useEffect, useCallback } from 'react';
import { Col, Row, Breadcrumb, Card, Spin, message, Descriptions, Table, Badge, Timeline } from 'antd';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { fetchAssetDetailsData, fetchTraceAssetData } from '../data/BlockchainApi';

function SupplyChainTrackingResults() {
    const { consortiumId, lotNumber } = useParams();
    const [asset, setAsset] = useState(null);
    const [traceEvents, setTraceEvents] = useState([]);
    const [loading, setLoading] = useState(true);
	const [selectedEvent, setSelectedEvent] = useState(null);


    const loadAssetDetails = useCallback(async () => {
        try {

            const [assetResponse, traceResponse] = await Promise.all([
                fetchAssetDetailsData(consortiumId, lotNumber),
                fetchTraceAssetData(consortiumId, lotNumber)
            ]);

            if (assetResponse) {
                setAsset(assetResponse.assetDetails);
            } else {
                message.error('Asset not found');
            }

            if (traceResponse?.events) {
				setTraceEvents(traceResponse.events);
				setSelectedEvent(traceResponse.events[0]);
			}

        } catch (error) {
            console.error('Error loading asset data:', error);
            message.error('Failed to load asset data');
        } finally {
            setLoading(false);
        }
    }, [consortiumId, lotNumber]);


    useEffect(() => {
        loadAssetDetails();
    }, [loadAssetDetails]);


    const eventIcons = {
        production: "🌾",
        transformation: "🏭",
        packaging: "📦",
        shipping: "🚚"
    };


    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }


    if (!asset) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <h2>Asset not found</h2>
            </div>
        );
    }


    return (
        <>
            <div className="breadcrumb" style={{ textAlign: "left", marginBottom: "50px" }}>
                <h1 style={{ color: "#102F10" }}>
                    <Breadcrumb
                        items={[
                            { title: <a href="/">Home</a> },
                            { title: <a href="/supply-chain-tracking">Supply Chain Tracking</a> },
                            { title: lotNumber }
                        ]}
                    />
                </h1>
            </div>

            <Row gutter={[32, 24]}>
                <Col xs={24}>

                    <Badge.Ribbon
                        text={asset.status}
                        color={"#102F10"}
                        style={{
                            fontSize: '16px',
                            height: '40px',
                            lineHeight: '40px',
                            padding: '0 22px',
                            zIndex: 1
                        }}
                    />

                    <Card
                        style={{
                            backgroundColor: "#FFF",
                            borderRadius: "8px",
                            padding: "24px",
                            boxShadow: '0 4px 12px rgba(0, 0, 0, .08)'
                        }}
                    >

                        <h2 style={{ marginBottom: 20, color: "#102F10" }}>
                            Asset Information
                        </h2>

                        <Descriptions bordered column={2} size="middle">

                            <Descriptions.Item label="Lot Number">
                                {asset.lotNumber}
                            </Descriptions.Item>

                            <Descriptions.Item label="GTIN">
                                {asset.gtin || "N/A"}
                            </Descriptions.Item>

                            <Descriptions.Item label="Status">
                                {asset.status}
                            </Descriptions.Item>

                            <Descriptions.Item label="Producer">
                                {asset.producerName || "N/A"}
                            </Descriptions.Item>

                            <Descriptions.Item label="Available Qty">
                                {asset.availableQuantity} {asset.uom}
                            </Descriptions.Item>

                            <Descriptions.Item label="Production Date">
                                {asset.assetExtension?.productionDate
                                    ? moment(asset.assetExtension.productionDate).format("DD/MM/YYYY HH:mm")
                                    : "N/A"}
                            </Descriptions.Item>

                            <Descriptions.Item label="Certified">
                                {asset.certified ? "Yes" : "No"}
                            </Descriptions.Item>

                            <Descriptions.Item label="Verified">
                                {asset.verified ? "Yes" : "No"}
                            </Descriptions.Item>

                        </Descriptions>


                        {asset.assetEnrichment?.inputLots?.length > 0 && (
                            <>
                                <h2 style={{ marginTop: 40, marginBottom: 16, color: "#102F10" }}>
                                    Input Lots
                                </h2>

                                <Table
									scroll={{ x: 'max-content' }}
                                    dataSource={asset.assetEnrichment.inputLots.map((row, index) => ({
                                        ...row,
                                        key: row.lotNumber || index
                                    }))}
                                    pagination={false}
                                    bordered
                                    columns={[
                                        { title: "Lot Number", dataIndex: "lotNumber" },
                                        { title: "GTIN", dataIndex: "GTIN" },
                                        { title: "Product Type", dataIndex: "inputProductType" },
                                        { title: "Quantity", dataIndex: "quantity" },
                                        { title: "UOM", dataIndex: "unitOfMeasure" },
                                        { title: "Description", dataIndex: "description" }
                                    ]}
                                />
                            </>
                        )}


                        {asset.assetEnrichment?.outputLots?.length > 0 && (
                            <>
                                <h2 style={{ marginTop: 40, marginBottom: 16, color: "#102F10" }}>
                                    Output Lots
                                </h2>

                                <Table
									scroll={{ x: 'max-content' }}
                                    dataSource={asset.assetEnrichment.outputLots.map((row, index) => ({
                                        ...row,
                                        key: row.lotNumber || index
                                    }))}
                                    pagination={false}
                                    bordered
                                    columns={[
                                        { title: "Lot Number", dataIndex: "lotNumber" },
                                        { title: "GTIN", dataIndex: "GTIN" },
                                        { title: "Quantity", dataIndex: "quantity" },
                                        { title: "UOM", dataIndex: "unitOfMeasure" },
                                        {
                                            title: "Production Date",
                                            dataIndex: "productionDate",
                                            render: (d) => d ? moment(d).format("DD/MM/YYYY") : "N/A"
                                        },
                                        { title: "Expiry", dataIndex: "expiryDate" },
                                        { title: "Best Before", dataIndex: "bestBeforeDate" }
                                    ]}
                                />
                            </>
                        )}


                        {asset.assetEnrichment?.shipmentInfo && (
                            <>
                                <h2 style={{ marginTop: 40, marginBottom: 16, color: "#102F10" }}>
                                    Shipment Information
                                </h2>

                                <Descriptions bordered column={2}>

                                    <Descriptions.Item label="Shipping Company">
                                        {asset.assetEnrichment.shipmentInfo.shippingCompanyName}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Shipping GLN">
                                        {asset.assetEnrichment.shipmentInfo.shippingCompanyGLN}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Ship From">
                                        {asset.assetEnrichment.shipmentInfo.shipFromAddress}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Ship To">
                                        {asset.assetEnrichment.shipmentInfo.shipToAddress}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Shipment Start">
                                        {moment(asset.assetEnrichment.shipmentInfo.shipmentStartTime).format("DD/MM/YYYY HH:mm")}
                                    </Descriptions.Item>

                                    <Descriptions.Item label="Shipment End">
                                        {moment(asset.assetEnrichment.shipmentInfo.shipmentEndTime).format("DD/MM/YYYY HH:mm")}
                                    </Descriptions.Item>

                                </Descriptions>
                            </>
                        )}


                        {asset.assetEnrichment?.shipmentInfo?.containerDetails?.length > 0 && (
                            <>
                                <h2 style={{ marginTop: 40, marginBottom: 16, color: "#102F10" }}>
                                    Container Details
                                </h2>

                                {asset.assetEnrichment.shipmentInfo.containerDetails.map((container, i) => (

                                    <Card
                                        key={`container-${i}`}
                                        variant="outlined"
                                        style={{ marginBottom: 16, background: "#FAFAFA" }}
                                    >

                                        <p>
                                            <strong>SSCC Codes:</strong> {container.SSCC}
                                        </p>

                                        <Descriptions bordered size="small" column={3}>

                                            {container.containerLotDetails.map((lot, j) => (
                                                <Descriptions.Item
                                                    key={`lot-${i}-${j}`}
                                                    label={lot.lotNumber}
                                                >
                                                    GTIN: {lot.GTIN}<br />
                                                    Qty: {lot.quantity || "N/A"}
                                                </Descriptions.Item>
                                            ))}

                                        </Descriptions>

                                    </Card>

                                ))}
                            </>
                        )}


                        {traceEvents.length > 0 && (
							<>
								<h2 style={{ marginTop: 40, marginBottom: 30, color: "#102F10" }}>
									Timeline
								</h2>

								<Row gutter={24}>
									{/* LEFT TIMELINE */}
									<Col xs={24} lg={4}>
										<Timeline
											items={traceEvents.map((group, index) => {
												const isSelected = selectedEvent?.eventName === group.eventName;

												return {
													color: group.events?.length > 0 ? "green" : "gray",
													children: (
														<div
															style={{
																cursor: "pointer",
																fontWeight: isSelected ? "bold" : "normal",
																padding: "6px",
																borderRadius: "6px",
																background: isSelected ? "#F6FFED" : "transparent",
																transition: "all 0.2s"
															}}
															onClick={() => setSelectedEvent(group)}
														>
															<strong style={{ textTransform: "capitalize" }}>
																{eventIcons[group.eventName] || "🔹"} {group.eventName}
															</strong>
														</div>
													)
												};
											})}
										/>
									</Col>

									{/* RIGHT EVENT DETAILS */}
									<Col xs={24} lg={20}>
										{!selectedEvent && (
											<Card>
												Select an event from the timeline
											</Card>
										)}

										{selectedEvent && (
											<Card>
												<h3 style={{ textTransform: "capitalize" }}>
													{eventIcons[selectedEvent.eventName] || "🔹"} {selectedEvent.eventName}
												</h3>
												<p>
													<strong>Entity:</strong> {selectedEvent.entityName}
												</p>
												{selectedEvent.events.map((event, i) => (
													<Card
														key={i}
														style={{ marginBottom: 16 }}
														type="inner"
														title={`Event ${i + 1}`}
													>
														<Descriptions bordered size="small" column={2}>
															<Descriptions.Item label="Event ID">
																{event.eventId}
															</Descriptions.Item>

															<Descriptions.Item label="Event Type">
																{event.eventType}
															</Descriptions.Item>
														</Descriptions>

														{event.displayAttributes?.length > 0 && (
															<>
																<h4 style={{ marginTop: 20 }}>Attributes</h4>

																<Descriptions bordered size="small" column={2}>
																	{event.displayAttributes.map((attr, index) => (

																		<Descriptions.Item
																			key={index}
																			label={attr.key}
																		>
																			{attr.type === "datetime"
																				? moment(attr.value).format("DD/MM/YYYY HH:mm")
																				: attr.value || "N/A"}
																		</Descriptions.Item>

																	))}
																</Descriptions>
															</>
														)}

														{event.additionalAttributes?.map((attr, idx) => {
															if (!Array.isArray(attr.value)) return null;

															return (
																<div key={idx} style={{ marginTop: 20 }}>
																	<h4>{attr.key}</h4>

																	<Table
																		scroll={{ x: 'max-content' }}
																		pagination={false}
																		size="small"
																		bordered
																		dataSource={attr.value.map((v, i) => ({
																			...v,
																			key: i
																		}))}
																		columns={Object.keys(attr.value[0]).map(key => ({
																			title: key,
																			dataIndex: key
																		}))}
																	/>
																</div>
															);
														})}
													</Card>
												))}
											</Card>
										)}
									</Col>

								</Row>
							</>
						)}
					</Card>
				</Col>
			</Row>
		</>
    );
}

export default SupplyChainTrackingResults;